import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Lock, Upload, CheckCircle2, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { API } from "../lib/api";

const ID_TYPES = [
  "Driver's License",
  "State ID",
  "Passport",
  "Permanent Resident Card",
  "Military ID",
  "Other Government ID",
];

const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/heic", "image/heif", "image/webp", "application/pdf"];
const MAX_MB = 10;

export default function VerifyId() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    date_of_birth: "",
    home_address: "",
    phone: "",
    id_type: ID_TYPES[0],
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.date_of_birth || !form.home_address) {
      toast.error("Please fill all required fields."); return;
    }
    if (!file) { toast.error("Please attach your ID document."); return; }
    if (!ACCEPTED_MIME.includes(file.type)) {
      toast.error("ID must be JPG, PNG, HEIC, WEBP, or PDF."); return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB}MB).`); return;
    }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("id_file", file);
    try {
      const res = await axios.post(`${API}/verify/id`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccess(res.data);
      toast.success("ID verification submitted successfully!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center px-4 py-16" data-testid="verify-success">
        <div className="max-w-xl w-full card-lux !p-10 text-center">
          <div className="h-16 w-16 rounded-full bg-[#10B981]/15 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-9 w-9 text-[#10B981]" />
          </div>
          <div className="eyebrow text-[#10B981] mt-6">ID Received</div>
          <h1 className="mt-3 font-serif text-4xl text-navy leading-tight">You're verified.</h1>
          <p className="mt-5 text-navy/70 leading-relaxed">
            Thanks — we've received your ID. Your placement coordinator will reach out within <strong>1 business day</strong> with your facial-verification call link.
          </p>
          <div className="mt-7 rounded-md bg-[#F6FDF9] border border-[#10B981]/25 p-4 text-sm text-navy/75 text-left flex items-start gap-3">
            <ShieldCheck className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div>
              <strong className="text-navy">What happens next:</strong> We'll match your ID to your CV on file{success.matched ? " (✓ already matched)" : ""} and email you a calendar link for the 15-min verification call.
            </div>
          </div>
          <a href="/" className="btn-emerald mt-8 inline-flex" data-testid="back-home-btn">Back to homepage <ArrowRight className="h-4 w-4" /></a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="verify-id-page">
      {/* HEADER */}
      <header className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-7 flex items-center gap-4">
          <div className="h-12 w-12 rounded-md bg-[#10B981]/20 flex items-center justify-center ring-1 ring-[#10B981]/40">
            <ShieldCheck className="h-6 w-6 text-[#10B981]" />
          </div>
          <div>
            <div className="eyebrow text-[#D4AF37]">Direct Hire Team</div>
            <h1 className="font-serif text-2xl md:text-3xl mt-1">Identity Verification</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12">
        <div className="card-lux !p-8 md:!p-10">
          <p className="text-navy/75 leading-relaxed">
            Upload your government-issued ID so we can finalize your employer match. Takes about 60 seconds. Your information is encrypted in transit and only viewable by our placement team.
          </p>

          <div className="mt-5 rounded-md border border-[#10B981]/25 bg-[#F6FDF9] p-4 flex items-start gap-3 text-sm text-navy/75">
            <Lock className="h-4 w-4 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div>
              <strong className="text-navy">Encrypted upload.</strong> We use your information solely to verify your identity for placement and run a background check (if you've consented).
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="verify-id-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="vfn">Full Legal Name *</Label>
                <Input id="vfn" data-testid="v-fullname" className="mt-2" value={form.full_name} onChange={update("full_name")} placeholder="As it appears on your ID" />
              </div>
              <div>
                <Label htmlFor="vem">Email Address *</Label>
                <Input id="vem" type="email" data-testid="v-email" className="mt-2" value={form.email} onChange={update("email")} placeholder="Same as on your application" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="vdob">Date of Birth *</Label>
                <Input id="vdob" type="date" data-testid="v-dob" className="mt-2" value={form.date_of_birth} onChange={update("date_of_birth")} />
              </div>
              <div>
                <Label htmlFor="vph">Phone Number</Label>
                <Input id="vph" type="tel" data-testid="v-phone" className="mt-2" value={form.phone} onChange={update("phone")} placeholder="(555) 123-4567" />
              </div>
            </div>

            <div>
              <Label htmlFor="vaddr">Home Address *</Label>
              <Textarea id="vaddr" rows={2} data-testid="v-address" className="mt-2" value={form.home_address} onChange={update("home_address")} placeholder="Street, City, State, ZIP" />
            </div>

            <div>
              <Label htmlFor="vidtype">ID Type *</Label>
              <select id="vidtype" data-testid="v-idtype" value={form.id_type} onChange={update("id_type")} className="mt-2 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <Label htmlFor="vfile">Upload ID Document *</Label>
              <label htmlFor="vfile" className="mt-2 block cursor-pointer rounded-md border-2 border-dashed border-navy/20 hover:border-[#10B981] bg-navy/[0.015] hover:bg-[#F6FDF9] transition-all p-6 text-center" data-testid="v-file-drop">
                <Upload className="h-7 w-7 text-navy/40 mx-auto" />
                <div className="mt-3 text-sm text-navy/75 font-medium">
                  {file ? file.name : "Click to choose a file"}
                </div>
                <div className="mt-1 text-[11px] text-navy/45">
                  JPG, PNG, HEIC, WEBP, or PDF · max {MAX_MB}MB
                </div>
                <input id="vfile" type="file" accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="v-file" />
              </label>
              {file && <p className="mt-1.5 text-xs text-[#10B981]">✓ {(file.size / 1024).toFixed(0)} KB selected</p>}
            </div>

            <button type="submit" disabled={submitting} className="btn-emerald w-full !py-3 disabled:opacity-60" data-testid="v-submit">
              {submitting ? "Submitting…" : "Submit Verification"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>

            <p className="text-[11px] text-navy/45 text-center">By submitting, you confirm this ID is yours and authorize Direct Hire Team to use it for identity verification.</p>
          </form>
        </div>

        <p className="text-center text-xs text-navy/45 mt-8">
          Questions? Email <a href="mailto:christopher@directhireteam.com" className="text-[#10B981] hover:underline">christopher@directhireteam.com</a>
        </p>
      </main>
    </div>
  );
}
